// Copyright ©️ 2026 eVolve MEP, LLC

import { type FacilityId, useEvolveGet, useEvolvePost } from '@evolvemep/api-ts';
import { Button, LoadingOverlay } from '@mantine/core';
import { Navigate, useParams } from 'react-router';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { isNil, isNotNil } from 'helpers/isNotNil';

import { ActiveWorkCellsGrid } from './ActiveWorkCellsGrid';
import { DraftWorkCellSetup } from './DraftWorkCellSetup';
import { useWorkCellSetupModals, WorkCellSetupModalProvider } from './modalRegistries';

type EditSetupButtonProps = {
  facilityId: FacilityId;
  loading: boolean;
  onDraftCreated: () => void;
};

const EditSetupButton = ({ facilityId, loading, onDraftCreated }: EditSetupButtonProps) => {
  const { apiCall: createDraftShopConfigurationApiCall, loading: creatingDraft } = useEvolvePost(
    `shop/configuration/${facilityId}/draft`,
  );
  const onClick = async () => {
    await createDraftShopConfigurationApiCall({});
    onDraftCreated();
  };
  return (
    <AgGridStyleTooltip
      withArrow
      openDelay={350}
      label="Add new work cells, remove existing work cells, and map tasks to work cells"
    >
      <Button
        variant="light"
        onClick={onClick}
        leftSection={<EvolveIcon icon="Edit" />}
        loading={creatingDraft || loading}
      >
        Edit setup
      </Button>
    </AgGridStyleTooltip>
  );
};

type DraftActionsProps = {
  facilityId: FacilityId;
  onDraftDiscarded: () => Promise<void>;
  onDraftApplied: () => Promise<void>;
};

const DraftActions = ({ facilityId, onDraftDiscarded, onDraftApplied }: DraftActionsProps) => {
  const { openModal } = useWorkCellSetupModals();
  return (
    <>
      <Button variant="light" onClick={() => openModal('discardDraftChangesModal', { facilityId, onDraftDiscarded })}>
        Discard
      </Button>
      <Button onClick={() => openModal('saveDraftChangesModal', { facilityId, onDraftApplied })}>Save changes</Button>
    </>
  );
};

export const WorkCellSetupPage = () => {
  const { facilityId: facilityIdRaw } = useParams();
  const facilityId = facilityIdRaw as FacilityId;
  const { data: facility, loading: loadingFacility } = useEvolveGet(`shop/facility/${facilityId}`, { lazy: false });
  const {
    data: shopConfigurations,
    loading: loadingShopConfigurations,
    apiCall: refreshShopConfigurations,
  } = useEvolveGet(`shop/configuration/${facilityId}`, { lazy: false });
  const {
    data: draftShopConfiguration,
    loading: loadingDraftShopConfiguration,
    apiCall: refreshDraftShopConfiguration,
  } = useEvolveGet(`shop/configuration/${facilityId}/draft`, { lazy: false });

  if (loadingFacility || loadingShopConfigurations) {
    return <LoadingOverlay visible />;
  }
  const activeShopConfiguration = shopConfigurations?.find((c) => c.isActive);
  if (isNil(activeShopConfiguration) || isNil(facility)) {
    // Should never happen in production,
    // but can happen in lower lifecycles due to bad seed data
    return <Navigate to="/not-found" />;
  }

  return (
    <WorkCellSetupModalProvider>
      <BasePageHeader
        title={facility.facilityName}
        parentPage={{
          title: 'Facilities',
          url: '/facilities',
        }}
        spacing="xl"
        topRightComponent={
          isNotNil(draftShopConfiguration) ? (
            <DraftActions
              facilityId={facilityId}
              onDraftDiscarded={async () => {
                await refreshDraftShopConfiguration();
              }}
              onDraftApplied={async () => {
                await refreshDraftShopConfiguration();
                void refreshShopConfigurations();
              }}
            />
          ) : (
            <EditSetupButton
              facilityId={facilityId}
              loading={loadingDraftShopConfiguration}
              onDraftCreated={() => {
                void refreshDraftShopConfiguration();
              }}
            />
          )
        }
      />
      {isNotNil(draftShopConfiguration) ? (
        <DraftWorkCellSetup
          facilityId={facilityId}
          activeShopConfiguration={activeShopConfiguration}
          draftShopConfiguration={draftShopConfiguration}
        />
      ) : (
        <ActiveWorkCellsGrid
          facilityId={facilityId}
          activeShopConfiguration={activeShopConfiguration}
          draftExists={false}
        />
      )}
    </WorkCellSetupModalProvider>
  );
};
